import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  WritableSignal,
  signal,
} from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Observable, Subscription, of, switchMap, take } from 'rxjs';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureInput } from '../../../core/models/question/question-input.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-input',
  templateUrl: './question-input.component.html',
  styleUrls: ['./question-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionInputComponent implements OnInit, OnChanges {
  value!: FormControl<string>;

  @Input() questionsInRow?: number;
  @Input() labelPrefix?: string;
  @Input() repeaterGroupId?: string;

  InputType = InputType;
  inputType!: InputType;
  questionNumber$!: Observable<unknown>;
  readonly readOnly$: Observable<ReadonlyState>;
  readonly activeLang$: Observable<string>;
  errorMessage$!: Observable<string | undefined>;
  suffixSymbol!: string;
  showChanges = input<boolean>();

  readonly readonlyValue: WritableSignal<string | undefined> =
    signal(undefined);
  readonly previousAnswersService = inject(PreviousAnswersService);
  valueChangeSubscription?: Subscription;
  patchValueSubscription?: Subscription;

  _questionForm!: FormGroup<ControlsOf<FormQuestionStandard>>;
  @Input({ required: true })
  set questionForm(form: FormGroup<ControlsOf<FormQuestionStandard>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }
  get questionForm() {
    return this._questionForm;
  }

  _question!: Question;
  @Input({ required: true })
  set question(value: Question) {
    this._question = value;
    const questionStructure = this._question
      .structure as QuestionStructureInput;
    switch (questionStructure.inputType) {
      case 'isPhone': {
        this.inputType = InputType.Phone;
        break;
      }
      case 'isNumber': {
        this.inputType = InputType.Number;
        break;
      }
      case 'isDiarynumber': {
        this.inputType = InputType.Diarienummer;
        break;
      }
      case 'isAmount': {
        this.inputType = InputType.Amount;
        break;
      }
      case 'isPercent': {
        this.inputType = InputType.Percent;
        break;
      }
      default: {
        this.inputType = InputType.Text;
        break;
      }
    }

    this.setSuffixSymbol();
  }
  get question() {
    return this._question;
  }

  constructor(
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private questionNumberService: QuestionNumberService,
    private formsQuery: RendererFormsQuery,
  ) {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;
  }

  ngOnInit() {
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );
    this.readOnly$.pipe(untilDestroyed(this)).subscribe((readonly) => {
      if (readonly) {
        this.setReadonly();
      }
    });

    this.setErrorMessage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      this.valueChangeSubscription?.unsubscribe();
      this.patchValueSubscription?.unsubscribe();

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());

      if (
        this.formsQuery.getReadOnly() &&
        !changes['repeaterGroupId'].firstChange
      ) {
        this.setReadonly();
      }
    }
  }

  get isTextInput(): boolean {
    return (
      (this.question.structure as QuestionStructureInput).inputType === 'isText'
    );
  }

  private setErrorMessage() {
    const value = this.value;
    this.errorMessage$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return this.formValidationService
              .questionInvalid(value, this.question.id)
              .pipe(
                switchMap((invalid) => {
                  if (invalid) {
                    if (value.errors?.validPhoneNumber) {
                      return this.translocoService.selectTranslate(
                        'phone_invalid',
                      );
                    }
                    if (value.errors?.required) {
                      return this.translocoService.selectTranslate('required');
                    }
                    if (value.errors?.validEmail) {
                      return this.translocoService.selectTranslate(
                        'email_validation_validEmail',
                      );
                    }
                    if (value.errors?.validWebpage) {
                      return this.translocoService.selectTranslate(
                        'webpage_validation_validWebpage',
                      );
                    }
                    if (value.errors?.validDiaryNumber) {
                      return this.translocoService.selectTranslate(
                        'diary_invalid',
                      );
                    }
                    if (value.errors?.maxlength) {
                      return this.inputType === InputType.Text
                        ? this.translocoService.selectTranslate(
                            'validation_maxlength',
                          )
                        : this.translocoService.selectTranslate(
                            'validation_maxlength_number',
                          );
                    }
                  }
                  return of(undefined);
                }),
              );
          }
          case ReadonlyState.Locked: {
            return this.errorMessage();
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );
  }

  errorMessage(): Observable<string | undefined> {
    const value = this.value;
    if (value?.errors?.validPhoneNumber) {
      return this.translocoService.selectTranslate('phone_invalid');
    }
    if (value?.errors?.required) {
      return this.translocoService.selectTranslate('field_required');
    }
    if (value?.errors?.validEmail) {
      return this.translocoService.selectTranslate(
        'email_validation_validEmail',
      );
    }
    if (value?.errors?.validWebpage) {
      return this.translocoService.selectTranslate(
        'webpage_validation_validWebpage',
      );
    }
    if (value?.errors?.validDiaryNumber) {
      return this.translocoService.selectTranslate('diary_invalid');
    }
    if (value?.errors?.maxlength) {
      return this.inputType === InputType.Text
        ? this.translocoService.selectTranslate('validation_maxlength')
        : this.translocoService.selectTranslate('validation_maxlength_number');
    }

    return of(undefined);
  }

  setReadonly() {
    if (this.inputType === InputType.Phone) {
      const value = this.value.value;
      return this.readonlyValue.set(
        value ? parsePhoneNumberFromString(value)?.formatNational() : undefined,
      );
    } else {
      this.readonlyValue.set(this.value.value);
    }
  }

  private setSuffixSymbol() {
    if (this.inputType === InputType.Amount) {
      this.suffixSymbol =
        (this.question.structure as QuestionStructureInput).currency ?? 'SEK';
    } else if (this.inputType === InputType.Percent) {
      this.suffixSymbol = '%';
    } else {
      this.suffixSymbol = '';
    }
  }
}
